import { useMemo, useState } from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AXIS_LINE, AXIS_TICK, ChartCard, ChartEmpty, GRID, SeriesToggle, readToggle, storeToggle } from '@/pages/GamAdvertisers/components/charts';
import type { LLDay } from '@/types/lead-lifetime';
import { useMoney } from './currency';
import { fmtLL } from './format';

const ARPU_COLOR = '#10b981';
const CPU_COLOR = '#ef4444';

function DayTooltip({ active, payload }: any) {
    const money = useMoney();
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as LLDay;
    return (
        <div className="rounded-lg border border-slate-700 bg-slate-800 p-3 text-white shadow-xl">
            <p className="mb-1 text-xs font-semibold">{fmtLL.date(d.date)}{d.partial ? ' (dia em andamento)' : ''}</p>
            <p className="text-xs"><span style={{ color: ARPU_COLOR }}>●</span> ARPU: <b className="tabular-nums">{money.unit(d.arpu)}</b></p>
            <p className="text-xs"><span style={{ color: CPU_COLOR }}>●</span> Custo por usuário: <b className="tabular-nums">{money.unit(d.cost_per_user)}</b></p>
            <p className="mt-1 text-[10px] opacity-70">
                receita {money.fmt(d.revenue)} · gasto {money.fmt(d.spend)} · {fmtLL.num(d.active_leads)} leads ativos
            </p>
        </div>
    );
}

/**
 * ARPU × custo real por usuário, dia a dia. "Usuário" = lead que clicou naquele dia
 * (qualquer idade). ARPU acima do custo = margem positiva no dia.
 */
export function DailyChart({ days }: { days: LLDay[] }) {
    const money = useMoney();
    const [showArpu, setShowArpu] = useState(() => readToggle('lltv_daily_arpu', true));
    const [showCpu, setShowCpu] = useState(() => readToggle('lltv_daily_cpu', true));
    const data = useMemo(() => days.filter((d) => d.active_leads > 0 || d.spend > 0), [days]);

    return (
        <ChartCard
            title="ARPU × custo real por usuário"
            subtitle="Por dia · usuário = lead que clicou no dia · ARPU = receita líquida ÷ usuários · custo = gasto ÷ usuários"
            right={
                <div className="flex gap-1.5">
                    <SeriesToggle label="ARPU" color={ARPU_COLOR} active={showArpu} onChange={(v) => { setShowArpu(v); storeToggle('lltv_daily_arpu', v); }} hint="Receita líquida do dia dividida pelos leads que clicaram no dia" />
                    <SeriesToggle label="Custo/usuário" color={CPU_COLOR} active={showCpu} onChange={(v) => { setShowCpu(v); storeToggle('lltv_daily_cpu', v); }} hint="Gasto com anúncio do dia dividido pelos leads que clicaram no dia" />
                </div>
            }
        >
            {data.length === 0 ? (
                <ChartEmpty height={260} />
            ) : (
                <div style={{ height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={data} margin={{ top: 8, right: 12, left: 4, bottom: 0 }}>
                            <CartesianGrid {...GRID} vertical={false} />
                            <XAxis dataKey="date" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={false} tickFormatter={fmtLL.shortDate} />
                            <YAxis tick={AXIS_TICK} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => money.fmt(Number(v))} />
                            <Tooltip content={<DayTooltip />} />
                            {showCpu && <Line type="monotone" dataKey="cost_per_user" name="Custo/usuário" stroke={CPU_COLOR} strokeWidth={2} dot={false} connectNulls />}
                            {showArpu && <Line type="monotone" dataKey="arpu" name="ARPU" stroke={ARPU_COLOR} strokeWidth={2.5} dot={false} connectNulls />}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </ChartCard>
    );
}
